import React, { useState, useMemo } from "react";
import { Polygon } from "react-leaflet";

import MapConfig from "services/map/MapConfig";
import MapTooltipLabel from "components/MapTooltipLabel";

type Props = {
  label?: string | JSX.Element | null;
  disabled: boolean;
  selected: boolean;
  onClick: Function;
  options: {
    selection_color?: string;
    withLabel?: boolean;
    withLabelBackground?: boolean;
    tooltipCoordinates?: [];
    area_id?: string;
    background?: {
      value?: number;
      total?: number;
      out?: number;
      in?: number;
      config?: {
        mainColor: string;
        unselectedColor: string;
        minColor: string;
        valueColors: {};
        unselectedValueColor: string;
      };
    };
  };
  positions?: [];
  interactive?: boolean;
};

function MapArea({
  label,
  disabled,
  selected,
  onClick,
  options,
  positions,
  interactive = true,
}: Props): JSX.Element {
  const [leafletInstance, setLeafletInstance] = useState<{
    leafletElement: { getBounds: Function };
  }>();

  const computedSelected = useMemo(() => {
    return !disabled && selected;
  }, [disabled, selected]);

  const color = useMemo(() => {
    if (disabled) {
      return MapConfig?.flow?.area?.color?.disabled;
    }
    if (selected) {
      return options?.selection_color || MapConfig?.flow?.area?.color?.selected;
    }

    return MapConfig?.flow?.area?.color?.unselected;
  }, [disabled, selected, options]);

  const fillColor = useMemo(() => {
    if (disabled) {
      return MapConfig?.flow?.area?.color?.disabled;
    }
    if (selected) {
      return (
        options?.selection_color || MapConfig?.flow?.area?.fillColor?.selected
      );
    }

    return MapConfig?.flow?.area?.fillColor?.unselected;
  }, [disabled, selected, options]);

  const fillOpacity = useMemo(() => {
    if (disabled) {
      return MapConfig?.flow?.area?.fillOpacity?.disabled;
    }
    if (selected) {
      return MapConfig?.flow?.area?.fillOpacity?.selected;
    }

    return MapConfig?.flow?.area?.fillOpacity?.unselected;
  }, [disabled, selected]);

  const dashArray = useMemo(() => {
    if (disabled) {
      return "20, 20";
    }

    return undefined;
  }, [disabled]);

  // For Area, we use a position for the toolTip in lat/lng without offset
  const tooltipPosition = useMemo(() => {
    if (!options?.tooltipCoordinates) {
      return leafletInstance?.leafletElement?.getBounds()?.getCenter();
    }

    return options?.tooltipCoordinates;
  }, [leafletInstance?.leafletElement, options?.tooltipCoordinates]);

  function handleOnClick() {
    if (disabled) return null;

    return onClick();
  }

  return (
    <>
      <Polygon
        ref={(ref: any) => {
          setLeafletInstance(ref);
        }}
        // @ts-ignore
        positions={positions}
        fillColor={fillColor}
        fillOpacity={fillOpacity}
        color={color}
        dashArray={dashArray}
        onClick={handleOnClick}
        interactive={interactive}
      />
      {options?.withLabel ? (
        <MapTooltipLabel
          type="area"
          label={label}
          position={tooltipPosition}
          selected={computedSelected}
          background={
            options?.withLabelBackground ? options?.background : undefined
          }
          disabled={disabled}
          onClick={onClick}
          interactive={interactive}
        />
      ) : null}
    </>
  );
}

MapArea.defaultProps = {
  disabled: false,
  onClick: () => {},
  options: {},
  selected: false,
};

export default MapArea;
